import { inject, Injectable, NgZone, signal } from '@angular/core';
import { Map } from 'maplibre-gl';

/**
 * Owns everything about in-map navigation mode: the GPS follow loop (rAF camera control), the
 * compass/device-orientation heading, the screen wake-lock and the speed→zoom logic.
 *
 * While active it runs its own navigator.geolocation.watchPosition as the location source, so it is
 * fully independent of the map's GeolocateControl (which stays owned by the map service for the
 * plain "current location" button). The map is handed over via attach() once it is initialized.
 * This one-way dependency (map service → NavigationService, never the reverse) keeps DI acyclic.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly ngZone = inject(NgZone);

  /** The single source of truth for whether navigation mode is on. Read by the map service (bearing)
   *  and the map component (button state, label). */
  readonly active = signal(false);

  private map?: Map;
  private watchId?: number;

  private orientationListener?: (event: DeviceOrientationEvent) => void;
  private gpsHeadingActive = false;
  private wakeLock?: WakeLockSentinel;
  private visibilityListener?: () => void;
  private following = false;
  private lastLocation?: { lng: number; lat: number };
  private targetHeading?: number;
  private targetZoom?: number;
  private followFrame?: number;
  private followLastTs?: number;
  private locationHistory: { lng: number; lat: number; t: number }[] = [];
  private smoothedSpeedMps?: number;
  private lastSpeedTs?: number;

  /** Bind to a freshly-initialized map. Resets all navigation state. */
  attach(map: Map): void {
    this.detach();
    this.map = map;
  }

  /** Stop every running resource (location watch, rAF loop, listeners, wake-lock) and drop the map
   *  ref. Idempotent; safe to call when nothing is attached (map removed, destroyed, re-initialize). */
  detach(): void {
    this.stopWatch();
    this.stopFollowLoop();
    this.stopCompass();
    this.stopWakeLock();
    this.active.set(false);
    this.map = undefined;
    this.following = false;
    this.lastLocation = undefined;
    this.targetHeading = undefined;
    this.targetZoom = undefined;
    this.locationHistory = [];
    this.smoothedSpeedMps = undefined;
    this.lastSpeedTs = undefined;
  }

  /** Fed from the navigation-owned watchPosition while navigation mode is on. */
  onLocation(coords: GeolocationCoordinates, timestamp: number): void {
    const { longitude, latitude, heading } = coords;
    this.lastLocation = { lng: longitude, lat: latitude };

    // Zoom to keep a fixed time of travel visible ahead: faster ⇒ zoom out, slower ⇒ zoom in. Speed
    // prefers the device's own Doppler reading and falls back to displacement.
    this.recordLocation(longitude, latitude, timestamp);
    const speedMps = this.navigationSpeed(coords.speed, timestamp);
    this.targetZoom = this.zoomForSpeed(speedMps, latitude);
    // GPS course-over-ground is the primary heading source: it reflects the true direction of travel
    // and is immune to how the phone is tilted or held. It is null while standing still and noisy at
    // crawling speed, so we only adopt it above a small speed floor and otherwise hold the last
    // heading. The compass (below) is used only until GPS provides a heading.
    const MIN_HEADING_SPEED_MPS = 1.5; // ~5 km/h
    if (
      typeof heading === 'number' &&
      !Number.isNaN(heading) &&
      (speedMps === null || speedMps >= MIN_HEADING_SPEED_MPS)
    ) {
      this.targetHeading = heading;
      this.gpsHeadingActive = true;
    }
    // The follow loop eases the camera towards these targets; nothing to move here.
  }

  async toggleNavigationMode(): Promise<void> {
    if (this.active()) {
      this.disableNavigationMode();
    } else {
      await this.enableNavigationMode();
    }
  }

  private async enableNavigationMode(): Promise<void> {
    const map = this.map;
    if (!map) {
      return;
    }
    // Navigation supplies its own location stream; without the Geolocation API there is nothing to
    // follow, so leave the map as-is rather than spinning up the loop, wake-lock and compass.
    if (!('geolocation' in navigator)) {
      return;
    }

    map.dragRotate.enable();
    map.touchZoomRotate.enableRotation();
    map.keyboard.enableRotation();

    this.following = true;
    this.gpsHeadingActive = false;
    await this.startCompass();

    this.active.set(true);
    this.startWakeLock();
    this.startWatch();
    this.startFollowLoop();
  }

  disableNavigationMode(): void {
    const map = this.map;
    this.active.set(false);
    this.stopWatch();
    this.stopFollowLoop();
    this.stopCompass();
    this.stopWakeLock();
    this.following = false;
    this.lastLocation = undefined;
    this.targetHeading = undefined;
    this.targetZoom = undefined;
    this.locationHistory = [];
    this.smoothedSpeedMps = undefined;
    this.lastSpeedTs = undefined;

    if (map) {
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation();
      map.easeTo({ bearing: 0 });
    }
  }

  /** Start the navigation-owned location watch that feeds the follow loop. Idempotent. */
  private startWatch(): void {
    if (this.watchId != null) {
      return;
    }
    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.onLocation(position.coords, position.timestamp),
      (error) => {
        // A transient failure (temporary signal loss / timeout) leaves the watch running, so only a
        // hard permission denial tears navigation down.
        if (error.code === error.PERMISSION_DENIED) {
          this.disableNavigationMode();
        }
      },
      { enableHighAccuracy: true },
    );
  }

  private stopWatch(): void {
    if (this.watchId != null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = undefined;
    }
  }

  private async startCompass(): Promise<void> {
    // iOS 13+ gates the compass behind an explicit permission prompt that must be triggered by a
    // user gesture. Other browsers expose the events without a permission call.
    const orientationEvent = DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    if (typeof orientationEvent.requestPermission === 'function') {
      try {
        const permission = await orientationEvent.requestPermission();
        if (permission !== 'granted') {
          return;
        }
      } catch {
        // No compass access; the GPS heading fallback still applies.
        return;
      }
    }

    this.orientationListener = (event: DeviceOrientationEvent) => {
      const heading = this.getCompassHeading(event);
      if (heading === undefined) {
        return;
      }
      // The compass only orients the map until GPS supplies a heading. Once we are moving and GPS
      // course-over-ground is available it takes over, because the compass reports where the phone
      // points rather than where it travels — a tilted phone would otherwise rotate the map sideways.
      if (!this.gpsHeadingActive) {
        // The follow loop reads this target and eases the bearing towards it each frame.
        this.targetHeading = heading;
      }
    };
    window.addEventListener('deviceorientationabsolute', this.orientationListener as EventListener);
    window.addEventListener('deviceorientation', this.orientationListener);
  }

  private stopCompass(): void {
    if (this.orientationListener) {
      window.removeEventListener(
        'deviceorientationabsolute',
        this.orientationListener as EventListener,
      );
      window.removeEventListener('deviceorientation', this.orientationListener);
      this.orientationListener = undefined;
    }
  }

  private startWakeLock(): void {
    void this.acquireWakeLock();
    // The browser releases the lock whenever the page is hidden (tab switch, screen off). Re-acquire
    // it when the page becomes visible again while navigation is still active.
    this.visibilityListener = () => {
      if (document.visibilityState === 'visible' && this.active()) {
        void this.acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);
  }

  private async acquireWakeLock(): Promise<void> {
    // Not all browsers support the Screen Wake Lock API; navigation still works without it.
    if (!('wakeLock' in navigator)) {
      return;
    }
    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
    } catch {
      // Denied (e.g. low battery or not allowed); leave the screen to its default timeout.
    }
  }

  private stopWakeLock(): void {
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = undefined;
    }
    void this.wakeLock?.release();
    this.wakeLock = undefined;
  }

  private getCompassHeading(event: DeviceOrientationEvent): number | undefined {
    // Safari/iOS already reports a true compass heading in degrees clockwise from north.
    const iosHeading = (event as unknown as { webkitCompassHeading?: number }).webkitCompassHeading;
    if (typeof iosHeading === 'number' && !Number.isNaN(iosHeading)) {
      return this.normalizeHeading(iosHeading);
    }
    // Other browsers only give a usable heading from the absolute orientation, derived from alpha
    // and corrected for how the screen itself is rotated.
    if (event.absolute && event.alpha != null) {
      const screenAngle = screen.orientation?.angle ?? 0;
      return this.normalizeHeading(360 - event.alpha + screenAngle);
    }
    return undefined;
  }

  private normalizeHeading(heading: number): number {
    return ((heading % 360) + 360) % 360;
  }

  private startFollowLoop(): void {
    if (this.followFrame != null) {
      return;
    }
    this.followLastTs = undefined;
    // Run outside Angular so the per-frame camera updates don't trigger change detection.
    this.ngZone.runOutsideAngular(() => {
      const step = (timestamp: number) => {
        this.tickFollowCamera(timestamp);
        this.followFrame = requestAnimationFrame(step);
      };
      this.followFrame = requestAnimationFrame(step);
    });
  }

  private stopFollowLoop(): void {
    if (this.followFrame != null) {
      cancelAnimationFrame(this.followFrame);
      this.followFrame = undefined;
    }
  }

  private tickFollowCamera(timestamp: number): void {
    const map = this.map;
    if (!map || !this.active()) {
      return;
    }

    // Clamp dt so a long pause (e.g. a backgrounded tab) resumes gently instead of snapping.
    const dtMs = this.followLastTs == null ? 0 : Math.min(timestamp - this.followLastTs, 100);
    this.followLastTs = timestamp;
    if (dtMs <= 0) {
      return;
    }
    const dt = dtMs / 1000;

    const CENTER_TAU = 0.35;
    const BEARING_TAU = 0.45;
    // Zoom eases asymmetrically: reveal the road ahead promptly when speeding up (zooming out), but
    // draw back in gently when slowing down so a brief stop (e.g. a red light) doesn't pump the
    // camera all the way in and back out.
    const ZOOM_OUT_TAU = 0.6;
    const ZOOM_IN_TAU = 2.5;
    const centerK = 1 - Math.exp(-dt / CENTER_TAU);
    const bearingK = 1 - Math.exp(-dt / BEARING_TAU);

    const center = map.getCenter();
    const bearing = map.getBearing();
    const zoom = map.getZoom();

    const following = this.following && !!this.lastLocation;
    const targetLng = following ? this.lastLocation!.lng : center.lng;
    const targetLat = following ? this.lastLocation!.lat : center.lat;
    const targetZoom = following && this.targetZoom != null ? this.targetZoom : zoom;
    const targetBearing = this.targetHeading ?? bearing;

    const zoomK = 1 - Math.exp(-dt / (targetZoom < zoom ? ZOOM_OUT_TAU : ZOOM_IN_TAU));
    const nextLng = center.lng + (targetLng - center.lng) * centerK;
    const nextLat = center.lat + (targetLat - center.lat) * centerK;
    const nextZoom = zoom + (targetZoom - zoom) * zoomK;
    const nextBearing = bearing + this.shortestAngleDiff(bearing, targetBearing) * bearingK;

    // Skip the render once everything has settled to avoid needless work while stationary.
    if (
      Math.abs(nextLng - center.lng) < 1e-7 &&
      Math.abs(nextLat - center.lat) < 1e-7 &&
      Math.abs(nextZoom - zoom) < 1e-3 &&
      Math.abs(nextBearing - bearing) < 0.1
    ) {
      return;
    }

    map.jumpTo(
      { center: [nextLng, nextLat], zoom: nextZoom, bearing: nextBearing },
      { geolocateSource: true },
    );
  }

  // Signed smallest rotation (degrees, -180..180) from one bearing to another, so easing takes the
  // short way round instead of spinning through 0/360.
  private shortestAngleDiff(from: number, to: number): number {
    return ((((to - from) % 360) + 540) % 360) - 180;
  }

  // Keeps a short rolling window of recent fixes so speed can be measured over distance travelled.
  private recordLocation(lng: number, lat: number, timestamp: number): void {
    const SPEED_WINDOW_MS = 6000;
    this.locationHistory.push({ lng, lat, t: timestamp });
    const cutoff = timestamp - SPEED_WINDOW_MS;
    this.locationHistory = this.locationHistory.filter((point) => point.t >= cutoff);
  }

  // Average speed (m/s) from the net displacement across the window divided by its elapsed time.
  // Uses the straight-line distance between the first and last fix rather than the cumulative path
  // length: standing still, GPS jitter makes the path wander metre-by-metre (a random walk that
  // sums up and fakes a large speed), while the endpoints stay close together and read ~0.
  // Returns null until there are at least two fixes spanning a positive interval.
  private speedFromHistory(): number | null {
    const points = this.locationHistory;
    if (points.length < 2) {
      return null;
    }
    const first = points[0];
    const last = points[points.length - 1];
    const distance = this.haversineMeters(first, last);
    const seconds = (last.t - first.t) / 1000;
    return seconds > 0 ? distance / seconds : null;
  }

  private haversineMeters(
    a: { lng: number; lat: number },
    b: { lng: number; lat: number },
  ): number {
    const EARTH_RADIUS_M = 6371000;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  // Best available ground speed (m/s) for navigation, smoothed with a time-constant EMA. The
  // device's own Doppler speed (coords.speed) is preferred when present: it has no window lag, so
  // the map stops zooming out the instant you actually stop. It is null while stationary or on
  // devices without it, so we fall back to the displacement estimate over the recent fix window.
  private navigationSpeed(rawSpeed: number | null, timestamp: number): number | null {
    const sample =
      typeof rawSpeed === 'number' && !Number.isNaN(rawSpeed) && rawSpeed >= 0
        ? rawSpeed
        : this.speedFromHistory();
    if (sample == null) {
      return this.smoothedSpeedMps ?? null;
    }
    const SPEED_TAU_MS = 2000;
    const dt = this.lastSpeedTs == null ? SPEED_TAU_MS : Math.max(timestamp - this.lastSpeedTs, 0);
    this.lastSpeedTs = timestamp;
    const alpha = 1 - Math.exp(-dt / SPEED_TAU_MS);
    this.smoothedSpeedMps =
      this.smoothedSpeedMps == null
        ? sample
        : this.smoothedSpeedMps + alpha * (sample - this.smoothedSpeedMps);
    return this.smoothedSpeedMps;
  }

  // Zoom that keeps a fixed *time* of travel visible ahead of the vehicle (a "time headway"): the
  // faster you go, the further ahead you need to see, so the map zooms out. Because map scale is
  // logarithmic this reads naturally at every speed — roughly one zoom level per doubling of speed —
  // rather than the uneven feel of a linear speed→zoom ramp. Clamped to a comfortable street/
  // motorway range.
  private zoomForSpeed(speedMps: number | null, lat: number): number {
    const MAX_ZOOM = 16.5; // stopped / slow
    const MIN_ZOOM = 13; // fast / motorway
    const HEADWAY_SECONDS = 22; // how much travel time to keep visible ahead of the vehicle
    const MIN_LOOKAHEAD_M = 150; // keep some context around when stopped / crawling
    const map = this.map;
    if (!map || speedMps == null || Number.isNaN(speedMps) || speedMps <= 0) {
      return MAX_ZOOM;
    }
    // MapLibre uses 512px tiles, so ground resolution (m/px) at latitude φ is
    // 40075016.686·cos φ / (512·2^z). Invert it for the zoom that fits the look-ahead distance into
    // the screen space between the centred vehicle and the top edge (≈ half the viewport height).
    const EQUATOR_MPP_AT_Z0 = 78271.517; // 40075016.686 / 512
    const halfViewportPx = Math.max(map.getContainer().clientHeight / 2, 1);
    const lookaheadM = Math.max(speedMps * HEADWAY_SECONDS, MIN_LOOKAHEAD_M);
    const metersPerPixel = lookaheadM / halfViewportPx;
    const zoom = Math.log2((EQUATOR_MPP_AT_Z0 * Math.cos((lat * Math.PI) / 180)) / metersPerPixel);
    return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
  }
}
